import moment from 'moment';
import classnames from 'classnames';

import CircularProgress from '@mui/material/CircularProgress';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { grey } from '@mui/material/colors';
import CloseIcon from '@mui/icons-material/Close';

import Spacings from '~/modules/ui-kit/spacings';
import ShiftDetailCardApplications from '~/components/ShiftDetailCardApplications';
import ShiftDetailCardRoutes from '~/components/ShiftDetailCardRoutes';
import ShiftDetailCardAssignmentStatus from '~/components/ShiftDetailCardAssignmentStatus';
import ShiftDetailCardApplicationStatus from '~/components/ShiftDetailCardApplicationStatus';
import { PRIVATE } from '~/components/ShiftDetailCardApplicationStatus/constants';
import ShiftDetailCardCancellation from '~/components/ShiftDetailCardCancellation';
import { useShiftIssues } from '~/hooks/api/useShiftIssues';
import styles from './styles.module.css';
import { getRouteApi, useNavigate } from '@tanstack/react-router';

const route = getRouteApi('/weekplan/$year/$week/$day/{-$shiftId}');

export const Shiftplan = () => {
  const params = route.useParams();
  const navigate = useNavigate();
  const { data: shiftData, isLoading } = useShiftIssues({
    id: parseInt(params.shiftId ?? '0', 10),
    expand: ['shift.turns.route', 'issues.turn'],
  });
  const hasPlanningErrors =
    !isLoading && shiftData?.issues
      ? shiftData?.issues.filter((turn) =>
          turn.issues
            ? turn.issues.filter((issue) => issue.severity === 'Error').length >
              0
            : false
        ).length > 0
      : false;
  return !shiftData || isLoading ? (
    <div
      style={{ backgroundColor: grey[300] }}
      className={classnames(styles.wrapper, styles.loading)}
    >
      <CircularProgress size={60} />
    </div>
  ) : (
    <div style={{ backgroundColor: grey[300] }} className={styles.wrapper}>
      <Toolbar
        style={{
          backgroundColor: grey[300],
          paddingLeft: 0,
        }}
      >
        <IconButton
          onClick={() => {
            const date = moment(shiftData.shift?.startDate);
            navigate({
              to: '/weekplan/$year/$week/$day/{-$shiftId}',
              params: {
                year: date.isoWeekYear().toString(),
                week: date.isoWeek().toString(),
                day: date.weekday().toString(),
                shiftId: undefined,
              },
            });
          }}
          size="large"
        >
          <CloseIcon />
        </IconButton>
        <Typography variant="body1" sx={{ ml: 1 }}>
          {`${moment(shiftData.shift?.startDate).format('llll')} - ${moment(shiftData.shift?.endDate).format('LT')}`}
        </Typography>
      </Toolbar>
      <div className={styles.content}>
        <Spacings.Inset scale="s">
          <Spacings.Stack>
            {shiftData.shift?.isCanceled ? (
              <ShiftDetailCardCancellation
                isCanceled={shiftData.shift?.isCanceled}
                shiftId={shiftData.shift?.id}
              />
            ) : null}
            <ShiftDetailCardApplicationStatus
              state={shiftData.shift.applicationState}
              shiftHasApplications={
                (shiftData.shift?.applications?.length ?? 0) > 0
              }
              shiftId={shiftData.shift.id}
            />
            {shiftData.shift?.applicationState !== PRIVATE && (
              <Spacings.Stack>
                <ShiftDetailCardAssignmentStatus
                  shiftId={shiftData.shift?.id}
                  state={shiftData.shift?.assignmentState}
                  shiftHasPlanningErrors={hasPlanningErrors}
                />
                <ShiftDetailCardApplications
                  assignedApplicationCount={
                    shiftData.shift?.assignedApplicationCount || 0
                  }
                  unassignedApplicationCount={
                    shiftData.shift?.unassignedApplicationCount || 0
                  }
                />
                <ShiftDetailCardRoutes
                  turns={shiftData.shift?.turns}
                  issues={shiftData.issues}
                  onPlanRoute={(turnId) => {
                    navigate({
                      to: '/weekplan/assignment/$shiftId/$turnId',
                      params: {
                        shiftId: shiftData.shift?.id.toString(),
                        turnId: turnId.toString(),
                      },
                      search: { from: undefined },
                    });
                  }}
                  onPlanRoutes={() => {
                    if (!shiftData.shift?.turns?.[0]) {
                      return;
                    }
                    navigate({
                      to: '/weekplan/assignment/$shiftId/$turnId',
                      params: {
                        shiftId: shiftData.shift?.id.toString(),
                        turnId: shiftData.shift?.turns[0].id.toString(),
                      },
                      search: { from: undefined },
                    });
                  }}
                />
                {!shiftData.shift?.isCanceled ? (
                  <ShiftDetailCardCancellation
                    isCanceled={shiftData.shift?.isCanceled || false}
                    shiftId={shiftData.shift?.id}
                  />
                ) : null}
              </Spacings.Stack>
            )}
          </Spacings.Stack>
        </Spacings.Inset>
      </div>
    </div>
  );
};

export default Shiftplan;
